import React from 'react';
import styled, { DefaultTheme, useTheme } from 'styled-components';
import { RadarChart, RadarDataset } from '../../charts/RadarChart';
import { AssessmentTestSessionDetailResponse } from '../../../api/admin';
import {
  TooltipContent,
  TooltipDetail,
  TooltipDetailHighlight,
  TooltipTitle,
} from '../Progress/Tooltip';
import { SmallPortalTooltip } from '../../Tooltip';
import {
  MetricValue,
  Title,
  MetricSection,
  MetricLabel,
  TooltipMarkdownContent,
  ScoreValueRow,
  ScoreValueLarge,
  ScoreMax,
  ConfidenceValueLarge,
  TextButton,
} from './common';

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Card = styled.div<{ statusColor?: string }>`
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  ${({ statusColor }) => statusColor && `border-top: 4px solid ${statusColor};`}
  border-radius: 8px;
  padding: 24px;
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const CardTitle = styled(Title)`
  padding-bottom: 16px;
  margin-bottom: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const VerticalDivider = styled.div`
  height: 60%;
  width: 1px;
  background: ${({ theme }) => theme.colors.bgActive};
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 0.01fr 3.4fr;
  gap: 16px;
  height: 100%;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
`;

const ResultItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
  justify-content: center;
`;

const InfoIcon = styled.i.attrs({
  className: 'fa-regular fa-circle-info',
})`
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 12px;
  min-width: 12px;
  opacity: 0.6;
  cursor: help;
  margin-left: 4px;

  &:hover {
    opacity: 1;
  }
`;

const ChartContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  position: relative;
  z-index: 1;
`;

const FingerprintWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  flex: 1;
`;

const ToggleContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
  padding: 8px 0;
`;

const ToggleButton = styled(TextButton)`
  font-size: 12px;
  font-weight: 400;
`;

// Get status color for the gradient
const getStatusColor = (theme: DefaultTheme, status: string | undefined) => {
  switch (status) {
    case 'assessed':
      return theme.colors.buttonPrimary;
    case 'completed':
      return theme.colors.highlight;
    case 'in-progress':
      return theme.colors.buttonSecondary;
    default:
      return theme.colors.textRegular;
  }
};

function TestOutcomes({
  test,
}: {
  test: AssessmentTestSessionDetailResponse | null | undefined;
}) {
  const shouldShowResults = test?.reviewedAt !== null;
  const confidence = test?.results?.overallSummary?.confidence ?? 0;

  return (
    <ResultItemsContainer>
      <MetricSection data-tooltip-id="overall-score">
        <MetricLabel>
          Overall Score
          <InfoIcon />
        </MetricLabel>
        <ScoreValueRow>
          <ScoreValueLarge>
            {shouldShowResults && test?.results?.overallSummary?.readinessScore
              ? test?.results?.overallSummary?.readinessScore
              : '-'}
          </ScoreValueLarge>
          {shouldShowResults &&
            test?.results?.overallSummary?.readinessScore && (
              <ScoreMax> / 10</ScoreMax>
            )}
        </ScoreValueRow>
      </MetricSection>
      <SmallPortalTooltip anchorSelect={`[data-tooltip-id="overall-score"]`}>
        <TooltipContent>
          <TooltipDetail>
            Computed as the average score across all tested skills
          </TooltipDetail>
        </TooltipContent>
      </SmallPortalTooltip>

      <MetricSection data-tooltip-id="confidence">
        <MetricLabel>
          Confidence
          <InfoIcon />
        </MetricLabel>
        <ConfidenceValueLarge confidence={confidence}>
          {shouldShowResults ? `${Math.round(confidence * 100)}%` : '-'}
        </ConfidenceValueLarge>
      </MetricSection>
      <SmallPortalTooltip anchorSelect={`[data-tooltip-id="confidence"]`}>
        <TooltipContent>
          <TooltipDetail>
            Determined by how many assessment objectives were covered throughout
            the test. Lower completion reduces confidence in the overall
            assessment
          </TooltipDetail>
        </TooltipContent>
      </SmallPortalTooltip>

      <MetricSection data-tooltip-id="readiness-level">
        <MetricLabel>
          Readiness Level
          <InfoIcon />
        </MetricLabel>
        <MetricValue style={{ textTransform: 'capitalize' }}>
          {(!shouldShowResults
            ? '-'
            : test?.results?.overallSummary?.readinessLevel) ?? '-'}
        </MetricValue>
      </MetricSection>
      <SmallPortalTooltip anchorSelect={`[data-tooltip-id="readiness-level"]`}>
        <TooltipContent>
          <TooltipDetail>
            Overall readiness level based on performance across all skills
          </TooltipDetail>
        </TooltipContent>
      </SmallPortalTooltip>
    </ResultItemsContainer>
  );
}

function Fingerprint({
  test,
}: {
  test: AssessmentTestSessionDetailResponse | null | undefined;
}) {
  const theme = useTheme();
  const shouldShowResults = test?.reviewedAt !== null;
  const [showCohortAverage, setShowCohortAverage] = React.useState(false);
  const [showTeamAverage, setShowTeamAverage] = React.useState(false);

  const hasCohortAverage =
    !!test?.cohortAverage && (test.cohortAverage?.count ?? 0) > 1;
  const hasTeamAverage =
    !!test?.teamAverage && (test.teamAverage?.count ?? 0) > 1;

  // Main fingerprint dataset
  const radarDataset: RadarDataset = {
    id: 'skills',
    label: 'Fingerprint',
    data:
      test?.dimensions.map((dimension) => {
        const skillResult = test?.results?.skillScores.find(
          (skill) => skill.skill === dimension.id
        );
        return {
          label: dimension.name,
          value: shouldShowResults ? skillResult?.score ?? null : null,
          tooltipChildren: shouldShowResults ? (
            <TooltipContent>
              <TooltipTitle>{dimension.name}</TooltipTitle>
              <TooltipDetail style={{ marginTop: '8px' }}>
                <TooltipDetailHighlight>Score</TooltipDetailHighlight>
              </TooltipDetail>
              <TooltipMarkdownContent
                md={`${skillResult?.score.toFixed(1) ?? '-'} / 10`}
              />
              <TooltipDetail style={{ marginTop: '8px' }}>
                <TooltipDetailHighlight>Level</TooltipDetailHighlight>
              </TooltipDetail>
              <TooltipMarkdownContent
                md={skillResult?.comparativeLevel ?? '-'}
              />
              <TooltipDetail style={{ marginTop: '8px' }}>
                <TooltipDetailHighlight>Confidence</TooltipDetailHighlight>
              </TooltipDetail>
              <TooltipMarkdownContent
                md={`${Math.round((skillResult?.confidence ?? 0) * 100)}%`}
              />
            </TooltipContent>
          ) : null,
        };
      }) ?? [],

    fillOpacity: 0.35,
    color: theme.colors.primary,
  };

  // Real cohort average dataset
  const cohortAverageDataset: RadarDataset = React.useMemo(
    () => ({
      id: 'cohort-average',
      label: 'Cohort Average',
      data:
        test?.dimensions.map((dimension) => {
          const cohortSkillAverage = test?.cohortAverage?.skillAverages.find(
            (skill) => skill.skill === dimension.id
          );
          const score = cohortSkillAverage?.score;
          return {
            label: dimension.name,
            value: shouldShowResults && score != null ? score : null,
            tooltipChildren:
              shouldShowResults && score != null ? (
                <TooltipContent>
                  <TooltipTitle>{dimension.name}</TooltipTitle>
                  <TooltipDetail style={{ marginTop: '8px' }}>
                    <TooltipDetailHighlight>
                      Cohort Average
                    </TooltipDetailHighlight>
                  </TooltipDetail>
                  <TooltipMarkdownContent md={score.toFixed(1)} />
                </TooltipContent>
              ) : null,
          };
        }) ?? [],
      fillOpacity: 0,
      strokeWidth: 2,
      color: theme.colors.secondary,
    }),
    [
      test?.dimensions,
      shouldShowResults,
      test?.cohortAverage,
      theme.colors.secondary,
    ]
  );

  // Real team average dataset
  const teamAverageDataset: RadarDataset = React.useMemo(
    () => ({
      id: 'team-average',
      label: 'Team Average',
      data:
        test?.dimensions.map((dimension) => {
          const teamSkillAverage = test?.teamAverage?.skillAverages.find(
            (skill) => skill.skill === dimension.id
          );
          const score = teamSkillAverage?.score;
          return {
            label: dimension.name,
            value: shouldShowResults && score != null ? score : null,
            tooltipChildren:
              shouldShowResults && score != null ? (
                <TooltipContent>
                  <TooltipTitle>{dimension.name}</TooltipTitle>
                  <TooltipDetail style={{ marginTop: '8px' }}>
                    <TooltipDetailHighlight>
                      Team Average
                    </TooltipDetailHighlight>
                  </TooltipDetail>
                  <TooltipMarkdownContent md={score.toFixed(1)} />
                </TooltipContent>
              ) : null,
          };
        }) ?? [],
      fillOpacity: 0,
      strokeWidth: 2,
      color: theme.colors.highlight,
    }),
    [
      test?.dimensions,
      shouldShowResults,
      test?.teamAverage,
      theme.colors.highlight,
    ]
  );

  // Build datasets array - main first for labels, overlays after
  const datasets = [
    radarDataset,
    ...(showCohortAverage ? [cohortAverageDataset] : []),
    ...(showTeamAverage ? [teamAverageDataset] : []),
  ];

  return (
    <FingerprintWrapper>
      <ChartContainer style={{ flex: 1 }}>
        <RadarChart
          style={{ paddingTop: 16, minHeight: 400 }}
          datasets={datasets}
          max={10}
          levels={5}
          size={450}
        />
      </ChartContainer>
      <ToggleContainer>
        <ToggleButton
          active={showCohortAverage}
          activeColor={theme.colors.secondary}
          hoverColor={theme.colors.secondary}
          onClick={() =>
            hasCohortAverage && setShowCohortAverage(!showCohortAverage)
          }
          disabled={!hasCohortAverage}
          data-tooltip-id="cohort-average-toggle"
        >
          Cohort Average
        </ToggleButton>
        <SmallPortalTooltip
          anchorSelect={`[data-tooltip-id="cohort-average-toggle"]`}
        >
          <TooltipContent>
            {hasCohortAverage ? (
              <>
                <TooltipTitle>Cohort Average Score</TooltipTitle>
                <TooltipMarkdownContent
                  md={test?.cohortAverage?.totalScore.toFixed(1) ?? '-'}
                />
                <TooltipDetail style={{ marginTop: '8px' }}>
                  Averaged across {test?.cohortAverage?.count ?? 0}{' '}
                  {test?.cohortAverage?.count === 1 ? 'person' : 'people'}
                </TooltipDetail>
              </>
            ) : (
              <TooltipDetail>Not enough data</TooltipDetail>
            )}
          </TooltipContent>
        </SmallPortalTooltip>

        <ToggleButton
          active={showTeamAverage}
          activeColor={theme.colors.highlight}
          hoverColor={theme.colors.highlight}
          onClick={() => hasTeamAverage && setShowTeamAverage(!showTeamAverage)}
          disabled={!hasTeamAverage}
          data-tooltip-id="team-average-toggle"
        >
          Team Average
        </ToggleButton>
        <SmallPortalTooltip
          anchorSelect={`[data-tooltip-id="team-average-toggle"]`}
        >
          <TooltipContent>
            {hasTeamAverage ? (
              <>
                <TooltipTitle>Team Average Score</TooltipTitle>
                <TooltipMarkdownContent
                  md={test?.teamAverage?.totalScore.toFixed(1) ?? '-'}
                />
                <TooltipDetail style={{ marginTop: '8px' }}>
                  Averaged across {test?.teamAverage?.count ?? 0}{' '}
                  {test?.teamAverage?.count === 1 ? 'person' : 'people'}
                </TooltipDetail>
              </>
            ) : (
              <TooltipDetail>Not enough data</TooltipDetail>
            )}
          </TooltipContent>
        </SmallPortalTooltip>
      </ToggleContainer>
    </FingerprintWrapper>
  );
}

export default function ResultsOverallFingerprint({
  test,
  status,
}: {
  test: AssessmentTestSessionDetailResponse | null | undefined;
  status: string;
}) {
  const theme = useTheme();

  return (
    <Column>
      <Card statusColor={getStatusColor(theme, status)}>
        <CardTitle>Assessment Results</CardTitle>
        <ResultsGrid>
          <TestOutcomes test={test} />
          <VerticalDivider />
          <Fingerprint test={test} />
        </ResultsGrid>
      </Card>
    </Column>
  );
}
